(() => {
  const root = document.querySelector('[data-klyyr-match-popup]');
  if (!(root instanceof HTMLElement)) return;

  const dialog = root.querySelector('[data-klyyr-match-dialog]');
  const grid = root.querySelector('[data-klyyr-match-grid]');
  const playView = root.querySelector('[data-klyyr-match-play]');
  const formView = root.querySelector('[data-klyyr-match-form]');
  const lostView = root.querySelector('[data-klyyr-match-lost]');
  const live = root.querySelector('[data-klyyr-match-live]');
  const formsHost = root.querySelector('[data-klyyr-match-forms-host]');
  const formsTemplate = root.querySelector('[data-klyyr-match-forms-template]');
  const portrait = root.querySelector('[data-klyyr-match-portrait]');
  const toothAccent = root.querySelector('[data-klyyr-match-tooth-accent]');

  const hydratePopupMedia = () => {
    if (root.dataset.mediaReady === 'true') return;

    if (root.dataset.bgUrl) {
      root.style.setProperty('--klyyr-match-bg', `url("${root.dataset.bgUrl}")`);
    }
    if (root.dataset.toothUrl) {
      root.style.setProperty('--klyyr-match-tooth', `url("${root.dataset.toothUrl}")`);
    }
    if (root.dataset.starUrl) {
      root.style.setProperty('--klyyr-match-star', `url("${root.dataset.starUrl}")`);
    }

    if (portrait instanceof HTMLImageElement) {
      if (portrait.dataset.srcset) portrait.srcset = portrait.dataset.srcset;
      if (portrait.dataset.src) portrait.src = portrait.dataset.src;
    }

    if (toothAccent instanceof HTMLImageElement && toothAccent.dataset.src) {
      toothAccent.src = toothAccent.dataset.src;
    }

    root.dataset.mediaReady = 'true';
  };

  const countEl = root.querySelector('[data-klyyr-match-count]');
  const againBtn = root.querySelector('[data-klyyr-match-again]');
  const burstLayer = root.querySelector('[data-klyyr-match-burst]');

  if (!(dialog instanceof HTMLDialogElement) || !grid || !playView || !formView || !lostView) {
    return;
  }

  const STORAGE_DISMISSED = 'klyyr-match-popup:dismissedAt';
  const formStatus = root.querySelector('[data-klyyr-match-form-status]');
  const retryButton = root.querySelector('[data-klyyr-match-retry]');

  const forceParam = new URLSearchParams(window.location.search).get('klyyr-match-popup');
  const forceOpen = forceParam === '1';
  const forceHide = forceParam === '0';
  const delayMs = Number(root.dataset.delayMs || 5000);
  const openTrigger = root.dataset.openTrigger || 'product_intent';
  const pageType = root.dataset.pageType || '';
  const dismissDays = Number(root.dataset.dismissDays || 14);
  const isPreview = root.dataset.preview === 'true';
  const skipLoggedIn = root.dataset.skipLoggedIn === 'true';
  const isLoggedIn = root.dataset.loggedIn === 'true';
  const hideHere = root.dataset.hideHere === 'true';
  const hiddenLabel = root.dataset.hiddenLabel || 'Hidden card';
  const matchedLive = root.dataset.matchedLive || 'You matched three cards. Enter your email for 10% off.';
  const lostLive = root.dataset.lostLive || 'No match this time.';
  const matchLabel = root.dataset.matchLabel || 'Matching card';
  const starLabel = root.dataset.starLabel || 'Bonus star';
  const copiedLabel = root.dataset.copiedLabel || 'Copied';

  let locked = false;
  let resolving = false;
  let allowClose = false;
  const tiles = [];

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const readStorage = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* private mode */
    }
  };

  const recentlyDismissed = () => {
    const raw = readStorage(STORAGE_DISMISSED);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    const maxAge = Math.max(1, dismissDays) * 24 * 60 * 60 * 1000;
    return Date.now() - at < maxAge;
  };

  const announce = (message) => {
    if (!(live instanceof HTMLElement)) return;
    live.textContent = '';
    window.requestAnimationFrame(() => {
      live.textContent = message;
    });
  };

  const setView = (view) => {
    playView.hidden = view !== 'play';
    formView.hidden = view !== 'form';
    lostView.hidden = view !== 'lost';
  };

  const flippedCount = () => tiles.filter((tile) => tile.dataset.flipped === 'true').length;

  const updateCount = () => {
    if (countEl instanceof HTMLElement) countEl.textContent = `${flippedCount()} / 4`;
  };

  const closePopup = (reason) => {
    allowClose = true;
    window.clearInterval(readinessTimer);
    if (dialog.open) dialog.close();
    document.body.classList.remove('klyyr-match-popup-open');
    if (reason === 'dismiss' && !isPreview) {
      writeStorage(STORAGE_DISMISSED, String(Date.now()));
    }
  };

  // Shopify Forms owns validation, submission, consent and the success/discount UI.
  // Inspect only readiness; never infer a successful submission from a click or timer.
  // Appearance is injected into the embed shadow root — theme CSS cannot reach it.
  const MATCH_FORM_CSS = `
    :host {
      --k-ink: #050505;
      --k-paper: #fafaf8;
      --k-sans: "Google Sans Flex", system-ui, sans-serif;
      --k-serif: "Instrument Serif", "Instrument Serif Fallback", Georgia, serif;
      --forms-body-font: normal 400 14px var(--k-sans);
      --forms-heading-font: italic 400 22px var(--k-serif);
      --forms-discount-code-color: #050505;
      --forms-discount-code-background-color-hover: #fafaf8;
      --forms-accent-color: #050505;
      --container-text-alignment: center;
      --inline-form-alignment: stretch;
      --heading-text-color: #050505;
      --body-text-color: #2a2a2a;
      --link-text-color: #050505;
      --form-text-color: #050505;
      --form-placeholder-color: #6e6e6e;
      --form-background-color: #ffffff;
      --button-text-color: #fafaf8;
      --button-background-color: #050505;
      --button-border-radius: 999px;
      --button-padding: 0px;
      --form-field-border-radius: 0px;
      --form-field-padding: 12px;
      --form-field-border: inset 0 0 0 1px #050505;
      --form-field-border-focused: inset 0 0 0 2px #050505;
      --container-max-w: 100%;
      --inline-container-max-width: 100%;
      --min-form-width: 0px;
      --app-embed-padding-x: 0;
      --app-embed-padding-y: 0;
      --spacing-size: 12px;
      --container-background-color: transparent;
      --container-shadow: none;
      --inline-container-min-height: 0px;
      font-family: var(--k-sans);
    }

    [data-form-root],
    [data-testid="app-router"],
    [class*="_inline"],
    [class*="_formContainer"],
    [class*="_grid"],
    [class*="_gridItemContent"],
    [class*="_container"] {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      text-align: center;
      justify-items: stretch;
    }

    form,
    [data-testid="form"] {
      display: flex !important;
      flex-direction: column;
      align-items: stretch !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }

    [data-current-step="form"] [class*="_formHeader"] {
      display: none !important;
    }

    h1, h2, h3, h4 {
      font-family: var(--k-serif) !important;
      font-style: italic;
      font-weight: 400 !important;
      letter-spacing: -0.02em;
      text-align: center;
      color: #050505 !important;
    }

    button, input, label, p, span, a {
      font-family: var(--k-sans) !important;
    }

    [class*="_formFieldContainer"] {
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      gap: 0.5rem;
      overflow: visible !important;
    }

    [class*="_formInputFieldLabel"],
    label[id^="label-"] {
      position: static !important;
      top: auto !important;
      left: auto !important;
      transform: none !important;
      width: auto !important;
      max-width: none !important;
      order: -1;
      margin: 0 !important;
      padding: 0 !important;
      font-size: 10px !important;
      line-height: 1.2 !important;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      text-align: left !important;
      color: #6e6e6e !important;
      pointer-events: none;
    }

    input,
    input[data-testid="field-email"] {
      box-sizing: border-box !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      height: 2.75rem !important;
      min-height: 2.75rem !important;
      padding: 0.65rem 0.85rem !important;
      font-size: 16px !important;
      overflow: visible !important;
      border: 1px solid #050505 !important;
      box-shadow: none !important;
      background: #ffffff !important;
    }

    button[type="submit"],
    [data-testid="btn-form-submit"] {
      box-sizing: border-box !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      align-self: center !important;
      flex: 0 0 auto !important;
      width: auto !important;
      min-width: 0;
      max-width: 100% !important;
      height: 2.75rem !important;
      min-height: 2.75rem !important;
      max-height: 2.75rem !important;
      padding: 0 1.5rem !important;
      letter-spacing: 0.04em;
      font-size: 13px !important;
      font-weight: 500 !important;
    }

    [class*="_formDisclaimer"] {
      display: none !important;
    }

    button[class*="_copyToClipboardButton"] {
      box-sizing: border-box !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 0.5rem;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      height: auto !important;
      min-height: 2.75rem !important;
      padding: 0.7rem 1rem !important;
      margin: 0.5rem 0 !important;
      overflow: visible !important;
      border: 1px solid #050505 !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      background: transparent !important;
      cursor: pointer;
    }

    span[class*="_copyToClipboardText"] {
      font-size: 15px !important;
      font-weight: 500 !important;
      letter-spacing: 0.08em;
      color: #050505 !important;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    button[class*="_copyToClipboardButton"].is-copied span[class*="_copyToClipboardText"] {
      letter-spacing: 0.08em;
      text-transform: none;
    }

    [class*="_copyIcon"] path {
      fill: #050505 !important;
    }
  `;

  const applyFormTheme = (embed) => {
    if (!(embed instanceof HTMLElement) || !embed.shadowRoot) return false;
    if (embed.dataset.klyyrFormTheme === '1') return true;
    const shadow = embed.shadowRoot;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(MATCH_FORM_CSS);
      shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet];
    } catch {
      if (!shadow.querySelector('style[data-klyyr-form-theme]')) {
        const style = document.createElement('style');
        style.dataset.klyyrFormTheme = 'true';
        style.textContent = MATCH_FORM_CSS;
        shadow.append(style);
      }
    }
    embed.dataset.klyyrFormTheme = '1';
    return true;
  };

  const formLooksSuccessful = (shadow) => {
    if (!shadow) return false;
    if (shadow.querySelector('[data-current-step="success"]')) return true;
    if (shadow.querySelector('button[class*="_copyToClipboard"]')) return true;
    const hasInput = shadow.querySelector('input:not([type="hidden"])');
    return !hasInput && /ENAMEL10/i.test(shadow.textContent || '');
  };

  const copyText = async (value) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      /* fall through */
    }
    try {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.left = '-9999px';
      document.body.append(field);
      field.select();
      const ok = document.execCommand('copy');
      field.remove();
      return ok;
    } catch {
      return false;
    }
  };

  const bindCopyButton = (embed) => {
    const shadow = embed && embed.shadowRoot;
    if (!shadow) return;
    shadow.querySelectorAll('button[class*="_copyToClipboard"]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement) || btn.dataset.klyyrCopyBound === '1') return;
      btn.dataset.klyyrCopyBound = '1';
      btn.addEventListener(
        'click',
        (event) => {
          const textEl =
            btn.querySelector('[class*="_copyToClipboardText"]') || btn.querySelector('span');
          const current = ((textEl && textEl.textContent) || '').trim();
          const code = (btn.dataset.klyyrCopyValue || current).replace(/^copied!?$/i, '').trim();
          if (!code) return;
          event.preventDefault();
          btn.dataset.klyyrCopyValue = code;
          copyText(code);
          btn.classList.add('is-copied');
          if (textEl) textEl.textContent = copiedLabel;
          window.setTimeout(() => {
            btn.classList.remove('is-copied');
            if (textEl && textEl.isConnected) textEl.textContent = code;
          }, 1800);
        },
        true
      );
    });
  };

  const syncFormState = (embed) => {
    const success = Boolean(embed && embed.shadowRoot && formLooksSuccessful(embed.shadowRoot));
    formView.classList.toggle('is-success', success);
    if (success) bindCopyButton(embed);
  };

  const watchEmbedState = (embed) => {
    if (!(embed instanceof HTMLElement) || !embed.shadowRoot) return;
    if (embed.dataset.klyyrStateObs === '1') {
      syncFormState(embed);
      return;
    }
    embed.dataset.klyyrStateObs = '1';
    const observer = new MutationObserver(() => syncFormState(embed));
    observer.observe(embed.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-current-step', 'class'],
    });
    syncFormState(embed);
  };

  const wireFormEmbed = (embed) => {
    applyFormTheme(embed);
    watchEmbedState(embed);
  };

  const watchFormTheme = () => {
    if (!formsHost) return;
    const styleEmbeds = () => {
      formsHost.querySelectorAll('shopify-forms-embed, shopify-forms-embed-dev').forEach(wireFormEmbed);
    };
    styleEmbeds();
    const observer = new MutationObserver(styleEmbeds);
    observer.observe(formsHost, { childList: true, subtree: true });
    window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
  };

  let readinessTimer = 0;
  const hasFormControls = (node) => {
    if (!node) return false;
    if (node.querySelector('input:not([type="hidden"]), button[type="submit"]')) return true;
    return [...node.querySelectorAll('*')].some((el) => el.shadowRoot && hasFormControls(el.shadowRoot));
  };

  const watchFormReadiness = () => {
    window.clearInterval(readinessTimer);
    const started = Date.now();
    const check = () => {
      const ready = hasFormControls(formsHost);
      if (formsHost) {
        formsHost.querySelectorAll('shopify-forms-embed, shopify-forms-embed-dev').forEach(wireFormEmbed);
      }
      if (formStatus instanceof HTMLElement) {
        formStatus.hidden = ready;
        formStatus.textContent = Date.now() - started < 10000
          ? root.dataset.formLoading
          : root.dataset.formUnavailable;
      }
      if (retryButton instanceof HTMLElement) retryButton.hidden = ready || Date.now() - started < 10000;
      if (ready || !dialog.open) window.clearInterval(readinessTimer);
    };
    readinessTimer = window.setInterval(check, 500);
    check();
  };

  const showForm = () => {
    locked = true;
    formView.classList.remove('is-success');
    setView('form');
    announce(matchedLive);
    formView.focus();
    if (formsHost instanceof HTMLElement && formsHost.dataset.mounted !== 'true') {
      if (formsTemplate instanceof HTMLTemplateElement && !formsHost.childElementCount) {
        formsHost.append(formsTemplate.content.cloneNode(true));
      }
      formsHost.dataset.mounted = 'true';
      if (typeof window.klyyrLoadFormsRuntime === 'function') window.klyyrLoadFormsRuntime();
    }
    watchFormReadiness();
  };


  const burstStars = () => {
    if (!(burstLayer instanceof HTMLElement)) return;
    burstLayer.replaceChildren();
    const count = 18;
    for (let i = 0; i < count; i += 1) {
      const star = document.createElement('span');
      star.className = 'klyyr-match-popup__star';
      const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const dist = 72 + Math.random() * 150;
      star.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      star.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
      star.style.setProperty('--rot', `${Math.floor(Math.random() * 90)}deg`);
      star.style.setProperty('--delay', `${Math.floor(Math.random() * 90)}ms`);
      star.style.setProperty('--star-size', `${12 + Math.floor(Math.random() * 14)}px`);
      star.style.setProperty('--star-color', i % 3 === 0 ? 'var(--k-ink)' : '#f0c93a');
      burstLayer.append(star);
    }
  };

  const celebrateWin = () => {
    tiles.forEach((tile) => tile.classList.add('is-win'));
    if (!reducedMotion()) burstStars();
    const wait = reducedMotion() ? 0 : 920;
    window.setTimeout(showForm, wait);
  };

  const flipTile = (tile) => {
    if (locked || resolving || tile.dataset.flipped === 'true') return;
    tile.dataset.flipped = 'true';
    tile.setAttribute('aria-pressed', 'true');
    tile.setAttribute('aria-label', matchLabel);
    updateCount();
    announce(`${flippedCount()} of 4 cards`);
    if (flippedCount() >= 3) {
      resolving = true;
      tiles.forEach((card) => {
        if (card.dataset.flipped !== 'true') {
          const face = card.querySelector('.klyyr-match-popup__tile-face--spec');
          if (face) {
            face.classList.remove('klyyr-match-popup__tile-face--spec');
            face.classList.add('klyyr-match-popup__tile-face--star');
          }
          card.dataset.kind = 'star';
          card.dataset.flipped = 'true';
          card.setAttribute('aria-pressed', 'true');
          card.setAttribute('aria-label', starLabel);
        }
      });
      updateCount();
      window.setTimeout(celebrateWin, reducedMotion() ? 0 : 480);
    }
  };

  const buildTiles = () => {
    grid.replaceChildren();
    if (burstLayer instanceof HTMLElement) burstLayer.replaceChildren();
    tiles.length = 0;
    locked = false;
    resolving = false;

    for (let index = 0; index < 4; index += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'klyyr-match-popup__tile';
      button.dataset.index = String(index);
      button.dataset.flipped = 'false';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `${hiddenLabel}, ${index + 1} of 4`);

      const inner = document.createElement('span');
      inner.className = 'klyyr-match-popup__tile-inner';
      inner.setAttribute('aria-hidden', 'true');

      const question = document.createElement('span');
      question.className = 'klyyr-match-popup__tile-face klyyr-match-popup__tile-face--question';
      const mark = document.createElement('span');
      mark.textContent = '?';
      question.append(mark);

      const specFace = document.createElement('span');
      specFace.className = 'klyyr-match-popup__tile-face klyyr-match-popup__tile-face--spec';

      inner.append(question, specFace);
      button.append(inner);
      button.addEventListener('click', () => flipTile(button));
      grid.append(button);
      tiles.push(button);
    }

    updateCount();
  };

  const openPopup = (view) => {
    hydratePopupMedia();
    buildTiles();
    setView(view);
    document.body.classList.add('klyyr-match-popup-open');
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    dialog.focus();
  };

  const playAgain = () => {
    buildTiles();
    setView('play');
    dialog.focus();
  };

  const shouldOpen = () => {
    if (forceHide) return false;
    if (hideHere) return false;
    if (isPreview) return true;
    if (forceOpen) return true;
    if (skipLoggedIn && isLoggedIn) return false;
    if (recentlyDismissed()) return false;
    return true;
  };

  root.querySelectorAll('[data-klyyr-match-close]').forEach((el) => {
    el.addEventListener('click', () => closePopup('dismiss'));
  });

  root.querySelectorAll('a.klyyr-match-popup__cta').forEach((el) => {
    el.addEventListener('click', () => {
      allowClose = true;
    });
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePopup('dismiss');
  });

  dialog.addEventListener('close', () => {
    const intentional = allowClose;
    allowClose = false;
    document.body.classList.remove('klyyr-match-popup-open');
    if (intentional) return;
    window.requestAnimationFrame(() => {
      if (!dialog.isConnected || dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.body.classList.add('klyyr-match-popup-open');
    });
  });

  if (againBtn instanceof HTMLButtonElement) {
    againBtn.addEventListener('click', playAgain);
  }

  if (retryButton instanceof HTMLButtonElement) {
    retryButton.addEventListener('click', () => window.location.reload());
  }
  window.addEventListener('pagehide', () => window.clearInterval(readinessTimer), { once: true });

  if (formsHost instanceof HTMLElement) {
    watchFormTheme();
  }

  const launcher = root.querySelector('[data-klyyr-match-open]');
  if (launcher instanceof HTMLButtonElement && !forceHide && !hideHere && !(skipLoggedIn && isLoggedIn)) {
    launcher.hidden = false;
    launcher.addEventListener('click', () => {
      // Safari does not focus buttons on pointer click; give the dialog a return target.
      launcher.focus({ preventScroll: true });
      if (!dialog.open) openPopup('play');
    });
  }

  if (!shouldOpen()) return;

  const openWhenReady = () => {
    if (!shouldOpen()) return;
    if (!dialog.open) openPopup('play');
  };

  const useIntent =
    openTrigger === 'intent' ||
    ((openTrigger === 'product_intent' || openTrigger === 'product_or_manual') && pageType === 'product');

  if (forceOpen || isPreview) {
    window.setTimeout(openWhenReady, 400);
  } else if (pageType === 'cart' || openTrigger === 'manual' || (openTrigger === 'product_or_manual' && pageType !== 'product')) {
    // Content and cart pages keep rewards available without interrupting reading or checkout.
    return;
  } else if (useIntent) {
    const onScroll = () => {
      const threshold = Math.min(window.innerHeight * 0.4, 480);
      if (window.scrollY < threshold) return;
      cleanupIntent();
      openWhenReady();
    };
    const onExit = (event) => {
      if (event.clientY > 0 || event.relatedTarget) return;
      cleanupIntent();
      openWhenReady();
    };
    const cleanupIntent = () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onExit);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseout', onExit);
  } else {
    window.setTimeout(openWhenReady, Math.max(0, delayMs));
  }
})();
